/**
 * A failed response as an Error carrying the API's own message, or the fallback when it has none.
 * The one place the SPA reads an error body: a refusal shows the gate's own sentence, which is what
 * tells it apart from an outage (and what the access matrix asserts, CLI-933). A body that isn't
 * JSON (a proxy's 502) falls back rather than throwing a parse error.
 */
export async function fail(
  res: { json: () => Promise<unknown> },
  fallback: string,
): Promise<never> {
  const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null
  throw new Error(body?.error?.message ?? fallback)
}

/**
 * A kept-alive response's body (the API's lib/keep-alive.ts): one that outlasted the edge's patience
 * was committed to a 200 before it knew how it ended, so a failure after that arrives as the error
 * envelope inside the 200. Thrown like any other failure, so a caller never shows it as data.
 */
export function throwIfLateError<T>(body: T, fallback: string): T {
  const late = body as { error?: { message?: string } } | null
  if (late && typeof late === 'object' && late.error && typeof late.error === 'object') {
    throw new Error(late.error.message ?? fallback)
  }
  return body
}
